import React from 'react';
import { Sparkle, ClipboardText, ArrowClockwise, Warning } from '@phosphor-icons/react';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import {
  AiReplySuggestion,
  MAX_AI_INSTRUCTION_LENGTH,
} from 'shared/api/ai-reply-suggestion';

interface Props {
  /** Het huidige voorstel, of `null` zolang er nog geen gevraagd is. */
  suggestion: AiReplySuggestion | null;
  isGenerating: boolean;
  /** Waarom het niet lukte; komt van de backend zodat het bruikbaar is. */
  error?: string | null;
  /** Wat de medewerker de AI meegeeft; leeg = geen instructie. */
  instruction: string;
  onInstructionChange: (instruction: string) => void;
  /** Zet het veld terug op de standaardinstructie. */
  onResetInstruction?: () => void;
  /** Of herstellen zinvol is: pas wanneer er van de standaardinstructie afgeweken is. */
  canResetInstruction?: boolean;
  onGenerate: () => void;
  /** Neemt de tekst over in het bericht. De aanroeper beslist hoe en waar. */
  onApply: (suggestion: AiReplySuggestion) => void;
  onDismiss: () => void;
  /** Uit wanneer er (nog) niets te antwoorden valt, met de reden in `disabledReason`. */
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Voorbeelden in de placeholder, zodat het veld niet als "prompt engineering" leest
 * maar als "zeg wat er in moet". Dat is wat een medewerker in gedachten heeft.
 */
const INSTRUCTIE_PLACEHOLDER =
  "Optioneel: wat moet er zeker in? Bv. \"meld dat de technieker dinsdag tussen 9 en 12u komt\", \"vraag foto's van de schade\", \"houd het kort en formeel\".";

/**
 * De knop **AI-voorstel** in een antwoordvenster, met het voorstel eronder.
 *
 * Bewust een apart blok bóven het invoerveld en niet meteen erín: zo blijft
 * zichtbaar welk stuk tekst van de AI komt en welk stuk je zelf schreef, en beslis
 * je zelf of je het overneemt.
 *
 * **Er vertrekt nooit iets vanzelf.** Deze knop schrijft tekst; verzenden gebeurt
 * met de verzendknop, door een mens. Dat staat er ook letterlijk bij — een gebruiker
 * moet niet hoeven gokken of hij net een mail naar een huurder heeft gestuurd.
 *
 * Wordt gebruikt door het antwoordvenster van de helpdesk en dat van de
 * renovatietickets; het component kent geen van beide en doet zelf geen enkele
 * aanroep.
 */
export const AiReplySuggestionPanel: React.FC<Props> = ({
  suggestion,
  isGenerating,
  error,
  instruction,
  onInstructionChange,
  onResetInstruction,
  canResetInstruction = false,
  onGenerate,
  onApply,
  onDismiss,
  disabled = false,
  disabledReason,
}) => {
  /**
   * Ctrl/⌘ + Enter schrijft het voorstel, zoals overal waar een tekstvak een knop
   * heeft. Een gewone Enter maakt een nieuwe regel — een instructie mag meerdere
   * puntjes bevatten.
   */
  const handleInstructionKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      if (!disabled && !isGenerating) onGenerate();
    }
  };

  return (
    <div className="border-moss-primary/30 bg-moss-primary/5 rounded-lg border border-dashed p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Sparkle size={15} weight="duotone" className="text-moss-primary" />
          <span className="text-neutral-primary text-sm font-medium">
            {suggestion ? 'Voorstel van de AI' : 'AI-voorstel'}
          </span>
          <span className="text-neutral-secondary-mid text-xs">
            {suggestion
              ? '— nalezen en aanpassen voor je verstuurt'
              : '— laat de AI een antwoord schrijven op basis van het mailverloop'}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onGenerate}
            disabled={disabled || isGenerating}
            title={
              disabled
                ? disabledReason
                : 'De AI leest de ticketgegevens en het mailverloop en schrijft een voorstel. Er wordt niets verstuurd.'
            }
          >
            {isGenerating ? (
              <>
                <ArrowClockwise size={14} className="animate-spin" />
                Aan het schrijven...
              </>
            ) : (
              <>
                <Sparkle size={14} weight="duotone" />
                {suggestion ? 'Opnieuw schrijven' : 'Schrijf een voorstel'}
              </>
            )}
          </Button>
          {suggestion && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onDismiss}
              className="text-neutral-secondary-mid"
            >
              Wegklikken
            </Button>
          )}
        </div>
      </div>

      {/* De instructie van de medewerker: wat er zeker in moet.

          Altijd zichtbaar en niet achter een "geavanceerd"-knop: zonder het veld te
          zien komt niemand op het idee dat het kan. Het staat standaard al gevuld met
          de huisregels (afsluiting, toon, om foto's vragen) — anders moet elke
          medewerker die elke keer opnieuw typen, of vergeet hij ze. */}
      <div className="mt-2">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <label
            htmlFor="ai-reply-instruction"
            className="text-neutral-primary text-xs font-medium"
          >
            Instructie voor de AI
          </label>
          {onResetInstruction && canResetInstruction && (
            <button
              type="button"
              onClick={onResetInstruction}
              disabled={disabled || isGenerating}
              className="text-neutral-secondary-mid hover:text-moss-primary text-xs underline disabled:opacity-50"
            >
              Standaard herstellen
            </button>
          )}
        </div>
        <Textarea
          id="ai-reply-instruction"
          value={instruction}
          onChange={(event) => onInstructionChange(event.target.value)}
          onKeyDown={handleInstructionKeyDown}
          placeholder={INSTRUCTIE_PLACEHOLDER}
          rows={3}
          maxLength={MAX_AI_INSTRUCTION_LENGTH}
          disabled={disabled || isGenerating}
          aria-label="Instructie voor de AI"
          className="resize-y bg-white text-sm"
        />
        <div className="text-neutral-secondary-mid mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span>
            Vul aan of pas aan wat er in het antwoord moet. Ctrl + Enter schrijft het
            voorstel.
          </span>
          {instruction.length > MAX_AI_INSTRUCTION_LENGTH - 100 && (
            <span>
              {instruction.length} / {MAX_AI_INSTRUCTION_LENGTH}
            </span>
          )}
        </div>
      </div>

      {disabled && disabledReason && (
        <p className="text-neutral-secondary-mid mt-2 text-xs">{disabledReason}</p>
      )}

      {error && (
        <p className="mt-2 flex items-start gap-1 text-xs text-amber-700">
          <Warning size={13} weight="fill" className="mt-0.5 shrink-0" />
          {error}
        </p>
      )}

      {suggestion && (
        <>
          <p className="text-neutral-primary mt-2 max-h-40 overflow-y-auto text-sm whitespace-pre-wrap">
            {suggestion.body}
          </p>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onApply(suggestion)}
            className="text-moss-primary hover:bg-moss-primary/10 mt-2"
          >
            <ClipboardText size={15} />
            Overnemen in het bericht
          </Button>
        </>
      )}
    </div>
  );
};
